import { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const upcoming = [
  {
    date: "11",
    month: "ABR",
    year: "2026",
    title: "Biomagnetismo Médico — Nivel 1",
    modality: "Online",
    modalityIcon: "ri-computer-line",
    location: "Zapopan, Jalisco",
    duration: "40 horas",
    spots: 8,
    totalSpots: 20,
    price: "$7,900MXN",
    tag: "Próximo",
    tagColor: "bg-[#D4AF37] text-[#0A1F44]",
    instructor: "Alonso Ashaika",
    img: "https://public.readdy.ai/ai/img_res/fd049a2d755a473125b04f25c48ccd54.jpg",
  },
  {
    date: "13",
    month: "May",
    year: "2026",
    title: "Método Yuen — Kinesiología Cuántica",
    modality: "Online",
    modalityIcon: "ri-computer-line",
    location: "Zoom / Grabación incluida",
    duration: "30 horas",
    spots: 15,
    totalSpots: 30,
    price: "$3,800 MXN",
    tag: "Online",
    tagColor: "bg-[#0A1F44] text-white",
    instructor: "Alonso Ashaika",
    img: "https://readdy.ai/api/search-image?query=quantum%20kinesiology%20online%20training%20webinar%20professional%20course%20laptop%20screen%20energy%20healing%20dark%20blue%20golden%20light%20modern%20setup&width=500&height=320&seq=upcoming002&orientation=landscape",
  },
  {
    date: "20",
    month: "JUN",
    year: "2026",
    title: "Hipnosis Terapéutica Avanzada",
    modality: "Presencial",
    modalityIcon: "ri-map-pin-line",
    location: "Zapopan, Jalisco",
    duration: "35 horas",
    spots: 5,
    totalSpots: 15,
    price: "$8,800 MXN",
    tag: "Últimos lugares",
    tagColor: "bg-red-500 text-white",
    instructor: "Alonso Ashaika",
    img: "https://readdy.ai/api/search-image?query=hypnotherapy%20clinical%20training%20professional%20seminar%20participants%20relaxation%20therapy%20room%20elegant%20warm%20lighting%20golden%20tones&width=500&height=320&seq=upcoming003&orientation=landscape",
  },
  {
    date: "30",
    month: "JUN",
    year: "2026",
    title: "Biotermodinamica Estructural",
    modality: "Online /",
    modalityIcon: "ri-global-line",
    location: "Híbrido",
    duration: "20 horas",
    spots: 20,
    totalSpots: 25,
    price: "$5,800MXN",
    tag: "Diplomado",
    tagColor: "bg-emerald-600 text-white",
    instructor: "Alonso Ashaika",
    img: "https://readdy.ai/api/search-image?query=biomagnetism%20tumor%20phenomenon%20advanced%20medical%20training%20professional%20diploma%20course%20dark%20blue%20golden%20background%20science%20energy%20medicine&width=500&height=320&seq=upcoming004&orientation=landscape",
  },
];

export default function UpcomingCourses() {
  const { ref, inView } = useInView();

  return (
    <section id="proximos-cursos" className="py-20 md:py-32 bg-[#F8F6F0]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className={`text-center mb-14 md:mb-20 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase">Agenda 2026</span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0A1F44] mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Próximos Cursos
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto">
            Reserva tu lugar con anticipación. Los cupos son limitados para garantizar una formación personalizada y de alta calidad.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {upcoming.map((course, i) => {
            const spotsLeft = course.spots;
            const pct = Math.round(((course.totalSpots - spotsLeft) / course.totalSpots) * 100);
            return (
              <div
                key={course.title}
                className={`bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#D4AF37]/40 transition-all duration-500 ${
                  inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {/* Image + date badge */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={course.img}
                    alt={course.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/70 to-transparent"></div>

                  {/* Date badge */}
                  <div className="absolute top-4 left-4 bg-white rounded-xl px-3 py-2 text-center min-w-[52px]">
                    <span className="block text-2xl font-black text-[#0A1F44] leading-none">{course.date}</span>
                    <span className="block text-[10px] font-bold text-[#D4AF37] tracking-widest uppercase">{course.month}</span>
                    <span className="block text-[10px] text-gray-400">{course.year}</span>
                  </div>

                  {/* Tag */}
                  <span className={`absolute top-4 right-4 text-xs font-bold px-3 py-1 rounded-full ${course.tagColor}`}>
                    {course.tag}
                  </span>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#0A1F44] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {course.title}
                  </h3>

                  <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4">
                    <span className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className={`${course.modalityIcon} text-[#D4AF37]`}></i>
                      {course.modality}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className="ri-map-pin-2-line text-[#D4AF37]"></i>
                      {course.location}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className="ri-time-line text-[#D4AF37]"></i>
                      {course.duration}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className="ri-user-line text-[#D4AF37]"></i>
                      {course.instructor}
                    </span>
                  </div>

                  {/* Spots bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-gray-500">Lugares disponibles</span>
                      <span className="text-xs font-bold text-[#0A1F44]">{spotsLeft} de {course.totalSpots}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#D4AF37] rounded-full transition-all duration-1000"
                        style={{ width: inView ? `${pct}%` : "0%" }}
                      ></div>
                    </div>
                  </div>

                  {/* Price + CTA */}
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <span className="text-xs text-gray-400 block">Inversión</span>
                      <span className="text-xl font-black text-[#0A1F44]">{course.price}</span>
                    </div>
                    <a
                      href={`https://wa.me/523325077743?text=Hola,%20quiero%20inscribirme%20al%20curso%20${encodeURIComponent(course.title)}%20del%20${course.date}%20de%20${course.month}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-[#0A1F44] text-white text-sm font-semibold px-5 py-3 rounded-xl hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line"></i>
                      Inscribirme
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        <div className={`text-center mt-12 transition-all duration-700 delay-500 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-gray-500 text-sm mb-4">¿Quieres que te avisemos cuando haya nuevas fechas disponibles?</p>
          <a
            href="https://wa.me/523325077743?text=Hola,%20quiero%20recibir%20información%20sobre%20próximas%20fechas%20de%20cursos%20en%20el%20Instituto%20Ashaika"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border-2 border-[#0A1F44] text-[#0A1F44] font-semibold px-7 py-3 rounded-full hover:bg-[#0A1F44] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-notification-line"></i>
            Notificarme de nuevas fechas
          </a>
        </div>
      </div>
    </section>
  );
}
